// export default BarChart;
import React, { Component } from 'react';
import * as d3 from 'd3';
import { format } from 'date-fns';
import { chart_metrics_units, formatChartMetricValue, formatChartThresholdValue, compactMetricNumber } from './utils_incident';

function formatDateToUTCString(date) {
  // Returns 'DD-MM-YYYY HH:mm' in UTC
  if (!(date instanceof Date)) return '';
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(date.getUTCDate())}-${pad(date.getUTCMonth() + 1)}-${date.getUTCFullYear()} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

// Robust UTC minute comparison for any date string or Date object
function isSameUtcMinute(a, b) {
  if (!a || !b) return false;

  function normalize(val) {
    if (val instanceof Date) {
      // Treat local wall-clock time as UTC
      return localToUtcFromFields(val);
    }

    if (typeof val === "string") {
      // Trim microseconds (keep only 3 digits if more are present)
      val = val.replace(/\.(\d{3})\d+$/, ".$1");

      // If no timezone → assume UTC
      if (!/z|[-+]\d{2}:?\d{2}|GMT/i.test(val)) {
        val += "Z";
      }
      return new Date(val);
    }

    return null;
  }

  const d1 = normalize(a);
  const d2 = normalize(b);

  if (!d1 || !d2 || isNaN(d1) || isNaN(d2)) {
   // console.log("❌ Invalid parse:", a, b);
    return false;
  }

  //console.log("✅ Parsed UTC:", d1.toISOString(), d2.toISOString());

  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate() &&
    d1.getUTCHours() === d2.getUTCHours() &&
    d1.getUTCMinutes() === d2.getUTCMinutes()
  );
}

function localToUtcFromFields(date) {
  return new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  ));
}

class BarChart extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
    this.tooltipRef = React.createRef();
    this.resizeObserver = null;
    this.debounceTimeout = null;
  }

  componentDidMount() {
    // Force render after mount to ensure parent dimensions are available
    setTimeout(() => {
      this.renderChart();
    }, 0);
    
    // Use ResizeObserver for responsive container monitoring
    this.resizeObserver = new ResizeObserver(() => {
      this.debouncedRender();
    });
    if (this.canvasRef.current?.parentElement) {
      this.resizeObserver.observe(this.canvasRef.current.parentElement);
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }

  // Debounced render to avoid excessive calls during resize
  debouncedRender = () => {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(() => {
      this.renderChart();
    }, 100); // 100ms debounce
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.chartData !== this.props.chartData ||
      prevProps.isActive !== this.props.isActive
    ) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        this.renderChart();
      }, 0);
    }
  }

  renderChart() {
    const { chartData, isActive = true } = this.props;
    // Normalize keys for all possible chartData shapes
    let xaxis = [];
    let yaxis = [];
    if (chartData) {
      if (Array.isArray(chartData.xaxis) && Array.isArray(chartData.yaxis)) {
        xaxis = chartData.xaxis;
        yaxis = chartData.yaxis;
      } else if (Array.isArray(chartData.x_axis) && Array.isArray(chartData.y_axis)) {
        xaxis = chartData.x_axis;
        yaxis = chartData.y_axis;
      } else if (Array.isArray(chartData.x) && Array.isArray(chartData.y)) {
        xaxis = chartData.x;
        yaxis = chartData.y;
      }
    }

    if (!isActive || !this.canvasRef.current || !xaxis.length) return;

    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Get the parent div's width and height for responsive chart
    const parentDiv = canvas.parentElement;

    // Use requestAnimationFrame to ensure parent has rendered
    requestAnimationFrame(() => {
      const fullWidth = (parentDiv?.clientWidth || 0) > 0 
        ? parentDiv.clientWidth 
        : 600;

      const fullHeight = (parentDiv?.clientHeight || 0) > 0 
        ? parentDiv.clientHeight 
        : 400;

      // Responsive margins and font sizes
      const isSmallScreen = fullWidth < 600;
      const margin = { 
        top: 10, 
        right: isSmallScreen ? 40 : 60, 
        bottom: isSmallScreen ? 25 : 35, 
        left: isSmallScreen ? 30 : 40 
      };
      const fontSize = isSmallScreen ? 10 : 12;
      const titleFontSize = isSmallScreen ? 11 : 13;

      const dpr = window.devicePixelRatio || 1;
      const width = fullWidth - margin.left - margin.right;
      const height = fullHeight - margin.top - margin.bottom;

      canvas.width = (width + margin.left + margin.right) * dpr;
      canvas.height = (height + margin.top + margin.bottom) * dpr;
      canvas.style.width = `${width + margin.left + margin.right}px`;
      canvas.style.height = `${height + margin.top + margin.bottom}px`;

      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const thresholds = chartData?.threshold || [];
      const data = xaxis.map((time, index) => {
        const utcDate = new Date(time);
        return {
          time: utcDate,
          value: yaxis[index],
          threshold: thresholds[index],
          formattedTime: formatDateToUTCString(utcDate)
        };
      });

      // Scales
      const xScale = d3
        .scaleBand()
        .domain(data.map((d) => d.formattedTime))
        .range([0, width])
        .padding(0.2);

      const yMax = d3.max(data, (d) => d.value) || 0;
      const yTicks = d3.scaleLinear().domain([0, yMax]).nice().ticks(5);
      const yStep = yTicks.length > 1 ? yTicks[1] - yTicks[0] : 1;
      const yAxisMax = yMax + 2 * yStep;
      const yScale = d3
        .scaleLinear()
        .domain([0, yAxisMax])
        .nice()
        .range([height, 0]);

      // Responsive bar width: scale with container, but cap it
      const barWidth = Math.min(xScale.bandwidth(), isSmallScreen ? 30 : 50);

      let metric = this.props.metricsData[0];
      let metricName = metric?.metrics_name || '';
      const metricsUnit = chart_metrics_units[metricName] !== undefined && chart_metrics_units[metricName] !== ''
        ? String(chart_metrics_units[metricName]).trim()
        : '';

      // Grid lines
      ctx.strokeStyle = '#334155';
      ctx.globalAlpha = 0.3;
      yScale.ticks(5).forEach((tick) => {
        const y = yScale(tick) + margin.top;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + width, y);
        ctx.stroke();
      });
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // Draw bars
      this.drawBars(data, xScale, yScale, barWidth, margin, ctx, height, width, metricsUnit, metricName, fontSize);

      // X-axis labels
      ctx.save();
      ctx.fillStyle = '#CBD5E1';
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';
      let lastDate = '';
      xScale.domain().forEach((tick, i) => {
        const currDate = format(new Date(xaxis[i]), 'yyyy-MM-dd');
        let label = '';
        if (currDate !== lastDate) {
          label = format(new Date(xaxis[i]), 'DD-MMM');
          lastDate = currDate;
        } else {
          label = format(new Date(xaxis[i]), 'HH:mm');
        }
        const x = margin.left + xScale(tick) + xScale.bandwidth() / 2;
        const y = margin.top + height + 8;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.fillText(label, x, y);
        ctx.restore();
      });
      ctx.restore();

      // Y-axis tick values on RIGHT side
      if (metricsUnit) {
        ctx.fillStyle = '#CBD5E1';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.font = `${fontSize}px sans-serif`;
        yScale.ticks(5).forEach((tick) => {
          const y = margin.top + yScale(tick);
          ctx.fillText(compactMetricNumber(tick, 1) ?? '0', margin.left + width + 6, y);
        });
      }

      // Y-axis title (vertical, right side)
      ctx.save();
      if (metricsUnit) {
        ctx.fillStyle = '#E2E8F0';
        ctx.font = `bold ${titleFontSize}px sans-serif`;
        ctx.translate(margin.left + width + (isSmallScreen ? 30 : 40), margin.top + height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`Values in ${metricsUnit}`, 0, 0);
        ctx.restore();
      }

      // Store these for hover interactions
      this.chartState = {
        data,
        xScale,
        yScale,
        barWidth,
        margin,
        ctx,
        height,
        width,
        metricsUnit,
        metricName,
        chartData,
        xaxis,
        fontSize
      };

      // Tooltip (no changes needed, but ensure it positions correctly on small screens)
      const tooltip = this.tooltipRef.current;
      let hoveredBarIndex = -1;
      canvas.onmousemove = (event) => {
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - margin.left;
        const mouseY = event.clientY - rect.top - margin.top;

        // Find which bar (rect) is hovered
        hoveredBarIndex = -1;
        data.forEach((d, i) => {
          const barX = xScale(d.formattedTime) + (xScale.bandwidth() - barWidth) / 2;
          const barY = yScale(d.value);
          if (
            mouseX >= barX &&
            mouseX <= barX + barWidth &&
            mouseY >= barY &&
            mouseY <= height
          ) {
            hoveredBarIndex = i;
          }
        });

        // Redraw chart to show hover
        this.renderChartWithHover(hoveredBarIndex);

        if (hoveredBarIndex !== -1) {
          const d = data[hoveredBarIndex];
          tooltip.style.opacity = 1;
          const chartWidth = canvas.width;
          const tooltipWidth = 220;
          let left = event.clientX - rect.left + 10;
          if (mouseX + margin.left > chartWidth / 2) {
            left = event.clientX - rect.left - tooltipWidth - 10;
            if (left < 0) left = 0;
          }
          tooltip.style.left = left + 'px';
          tooltip.style.top = (event.clientY - rect.top - 40) + 'px';
          
          let threshold = '';
          let metricName = '';
          let metricValue = '';
          if (Array.isArray(chartData?.metrics_details) && chartData.metrics_details.length > 0) {
            const metric = chartData.metrics_details.length === 1
              ? chartData.metrics_details[0]
              : chartData.metrics_details[hoveredBarIndex];
            threshold = metric?.threshold !== undefined ? metric.threshold : '';
            metricName = metric?.metrics_name || '';
            metricValue = metric?.value !== undefined ? metric.value : d.value;

            metricName =
              metricName === "Recency"
                ? "Missed Refresh"
                : metricName === "Row count"
                ? "Row Count Drift"
                : metricName === "Fill Rate"
                ? "Fill Rate Drift"
                : metricName;
          } else {
            metricValue = d.value;
          }
          threshold = d.threshold !== undefined ? d.threshold : threshold;
          if (this.props.metricsData.length > 0) {
            const metric = this.props.metricsData[0];
            metricName = metric?.metrics_name || '';

            metricName =
              metricName === "Recency"
                ? "Missed Refresh"
                : metricName === "Row count"
                ? "Row Count Drift"
                : metricName === "Fill Rate"
                ? "Fill Rate Drift"
                : metricName;
          }
          metricValue = formatChartMetricValue(metricValue, metricsUnit, { maximumFractionDigits: 2 });
          tooltip.innerHTML = `
            <div class="qd-incidents-tooltip">
              <div class="qd-incidents-tooltip__dialog">
                <h6>${format(d.time, 'MMM DD')}</h6>
                <small class="font-light">${format(d.time, 'YYYY-MM-DD HH:mm:ss')} GMT</small>
                <dl>
                  <dt>
                    <p>
                      Versions:
                      ${this.props.version_type === "" || this.props.version_type === undefined ? "NA" : this.props.version_type}
                    </p>
                  </dt>
                    ${metricName !== "Failed Runs" && metricName !== "Failed Stages" ? 
                    `<dt>
                      <p>
                        Threshold: ${formatChartThresholdValue(threshold)}
                      </p>
                    </dt>` : ''}
                  <dd>
                    <p>
                      <span class="dot text-danger"></span>
                      ${metricName}: ${metricValue}
                    </p>
                  </dd>
                </dl>
              </div>
            </div>
          `;
        } else {
          tooltip.style.opacity = 0;
          this.renderChartWithHover(-1);
        }
      };

      canvas.onmouseleave = () => {
        tooltip.style.opacity = 0;
        this.renderChartWithHover(-1);
      };
    });
  }

  // Updated drawBars to accept fontSize
  drawBars(data, xScale, yScale, barWidth, margin, ctx, height, width, metricsUnit, metricName, fontSize) {
    data.forEach((d, i) => {
      const xPos = margin.left + xScale(d.formattedTime) + (xScale.bandwidth() - barWidth) / 2;
      const yPos = margin.top + yScale(d.value);
      const barHeight = height - yScale(d.value);

      // Set color based on highlight
      let isHighlight = isSameUtcMinute(d.time, this.props.barHighlight);
      ctx.fillStyle = isHighlight ? '#F64E60' : '#ABB1C7';

      ctx.beginPath();
      ctx.roundRect(xPos, yPos, barWidth, barHeight, [6, 6, 0, 0]);
      ctx.fill();

      // Show value above bar
      if (metricsUnit) {
        ctx.fillStyle = 'white';
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        const label = metricName !== "Recency"
          ? formatChartMetricValue(d.value, metricsUnit, { maximumFractionDigits: 2 })
          : formatChartMetricValue(d.value, '', { maximumFractionDigits: 2 });
        ctx.fillText(
          label,
          xPos + barWidth / 2,
          yPos - 16
        );
      }
    });
  }

  renderChartWithHover(hoveredBarIndex) {
    if (!this.chartState) return;

    const { data, xScale, yScale, barWidth, margin, ctx, height, width, metricsUnit, metricName, fontSize } = this.chartState;

    // Clear only the chart area (not axes)
    ctx.clearRect(margin.left, margin.top, width, height);

    // Redraw grid lines
    ctx.strokeStyle = '#334155';
    ctx.globalAlpha = 0.3;
    yScale.ticks(5).forEach((tick) => {
      const y = yScale(tick) + margin.top;
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(margin.left + width, y);
      ctx.stroke();
    });
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;

    // Redraw bars
    this.drawBars(data, xScale, yScale, barWidth, margin, ctx, height, width, metricsUnit, metricName, fontSize);
  }

  render() {
    const { className = '', isActive = true } = this.props;

    if (!isActive) {
      return (
        <div className={`bg-chart-background rounded-xl p-6 ${className}`}>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-chart-secondary">Chart will render when active</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`bg-chart-background rounded-xl p-6 ${className}`}>
        <div 
          className="position-relative" 
          style={{ 
            overflow: 'visible',
            width: '100%', 
            height: this.props.height === undefined ? "280px" : this.props.height,
            minHeight: this.props.height === undefined ? "280px" : this.props.height,
            minWidth: '100%'
          }}
        >
          <canvas ref={this.canvasRef} style={{ display: 'block' }}></canvas>
          <div
            ref={this.tooltipRef}
            className="qd-incidents-tooltip-wrap"
            style={{
              opacity: 0,
              position: 'absolute',
              zIndex: 99999999999999,
              left: 40,
              top: 40,
              pointerEvents: 'none'
            }}
          ></div>
        </div>
      </div>
    );
  }
}
export default BarChart;